<script>
import { http } from "@/helpers/easyindustriaapi/config";

export default {
  name: "BirthdaysWeek",
  props: {
    empresaId: {
      type: [Number, String],
      default: null,
    },
    daysAhead: {
      type: Number,
      default: 7,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      upcoming: [],
    };
  },
  computed: {
    resolvedEmpresaId() {
      if (this.empresaId != null && this.empresaId !== "") return Number(this.empresaId);
      const currentEmpresa = JSON.parse(localStorage.getItem("currentEmpresa") || "null");
      return currentEmpresa?.id ? Number(currentEmpresa.id) : null;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.resolvedEmpresaId) {
        this.error = "Empresa não selecionada.";
        return;
      }

      this.loading = true;
      this.error = null;
      this.upcoming = [];

      try {
        const res = await http.get(`/professional?empresa_id=${this.resolvedEmpresaId}`);
        const professionals = Array.isArray(res.data) ? res.data : [];

        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const end = new Date(start);
        end.setDate(end.getDate() + Math.max(1, this.daysAhead));

        const upcoming = [];

        for (const p of professionals) {
          const bd = p?.birth_date;
          if (!bd) continue;

          const parts = String(bd).slice(0, 10).split("-");
          if (parts.length !== 3) continue;

          const month = Number(parts[1]); // 1-12
          const day = Number(parts[2]); // 1-31
          if (!month || !day) continue;

          let occ = new Date(start.getFullYear(), month - 1, day);
          if (occ < start) occ = new Date(start.getFullYear() + 1, month - 1, day);

          if (occ >= start && occ < end) {
            upcoming.push({
              id: p.id,
              name: p.name,
              occurrence: occ,
              dayLabel: this.formatDayLabel(occ),
            });
          }
        }

        upcoming.sort((a, b) => a.occurrence - b.occurrence);
        this.upcoming = upcoming;
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || "Erro ao carregar aniversariantes.";
      } finally {
        this.loading = false;
      }
    },
    formatDayLabel(date) {
      const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      return `${weekdays[date.getDay()]} • ${dd}/${mm}`;
    },
  },
};
</script>

<template>
  <b-card class="h-100 birthday-card">
    <div class="birthday-fx" aria-hidden="true">
      <span class="confetti c1"></span>
      <span class="confetti c2"></span>
      <span class="confetti c3"></span>
      <span class="confetti c4"></span>
      <span class="confetti c5"></span>
      <span class="balloon b1"></span>
      <span class="balloon b2"></span>
      <span class="balloon b3"></span>
    </div>
    <template #header>
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h5 class="mb-0">Aniversariantes da semana</h5>
          <small class="text-muted">Próximos {{ daysAhead }} dias</small>
        </div>
        <b-button size="sm" variant="outline-primary" :disabled="loading" @click="load">
          <b-spinner v-if="loading" small class="mr-1"></b-spinner>
          Atualizar
        </b-button>
      </div>
    </template>

    <b-alert v-if="error" variant="danger" show class="mb-3">{{ error }}</b-alert>

    <div v-if="!loading && !error && upcoming.length === 0" class="text-center py-4 text-muted">
      Nenhum aniversariante nos próximos {{ daysAhead }} dias.
    </div>

    <div v-if="upcoming.length" class="list-group list-group-flush">
      <div v-for="item in upcoming" :key="item.id" class="list-group-item d-flex align-items-center justify-content-between">
        <div class="text-truncate">
          <div class="font-weight-semibold text-truncate">{{ item.name }}</div>
          <small class="text-muted">{{ item.dayLabel }}</small>
        </div>
        <i class="ri-cake-2-line text-muted"></i>
      </div>
    </div>
  </b-card>
</template>

<style scoped>
.font-weight-semibold {
  font-weight: 600;
}

.birthday-card {
  position: relative;
  overflow: hidden;
}

.birthday-fx {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0;
  transition: opacity 180ms ease;
}

/* garante que o conteúdo fique acima do efeito */
.birthday-card :deep(.card-body),
.birthday-card :deep(.card-header) {
  position: relative;
  z-index: 1;
}

.birthday-card:hover .birthday-fx {
  opacity: 1;
}

.confetti,
.balloon {
  position: absolute;
  display: block;
  transform: translateZ(0);
}

/* Confetes */
.confetti {
  top: -12px;
  width: 10px;
  height: 14px;
  border-radius: 2px;
  opacity: 0.95;
  animation: confetti-fall 900ms ease-in-out infinite;
}

.confetti.c1 { left: 12%; background: #f72585; animation-delay: 0ms; }
.confetti.c2 { left: 28%; background: #ffd166; animation-delay: 120ms; }
.confetti.c3 { left: 52%; background: #4cc9f0; animation-delay: 240ms; }
.confetti.c4 { left: 71%; background: #7c3aed; animation-delay: 360ms; }
.confetti.c5 { left: 88%; background: #22c55e; animation-delay: 480ms; }

@keyframes confetti-fall {
  0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translateY(140px) rotate(320deg); opacity: 0; }
}

/* Balões */
.balloon {
  bottom: -44px;
  width: 34px;
  height: 44px;
  border-radius: 18px 18px 18px 18px;
  opacity: 0.9;
  animation: balloon-float 1200ms ease-in-out infinite;
  box-shadow: inset -6px -10px 0 rgba(0, 0, 0, 0.08);
}
.balloon::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -10px;
  width: 2px;
  height: 18px;
  background: rgba(0, 0, 0, 0.18);
  transform: translateX(-50%);
}
.balloon::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -3px;
  width: 8px;
  height: 6px;
  background: rgba(0, 0, 0, 0.14);
  border-radius: 0 0 6px 6px;
  transform: translateX(-50%);
}

.balloon.b1 { left: 8%; background: #ff5d8f; animation-delay: 0ms; }
.balloon.b2 { left: 16%; background: #56cfe1; animation-delay: 180ms; }
.balloon.b3 { left: 24%; background: #ffd166; animation-delay: 360ms; }

@keyframes balloon-float {
  0%   { transform: translateY(0) rotate(-2deg); opacity: 0; }
  15%  { opacity: 0.95; }
  100% { transform: translateY(-170px) rotate(2deg); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .confetti,
  .balloon {
    animation: none !important;
  }
  .birthday-fx {
    transition: none !important;
  }
}
</style>
